// src/pages/DruckAuswahl.jsx
// WAS SOLL GEDRUCKT WERDEN? (Anna-Lena 14.10.2026, Migration 77)
//
// Vor dem Erzeugen eines Kundendokuments (Vollprotokoll oder Kundenbericht)
// waehlt das Buero hier, welche Pruefpunkte im PDF erscheinen. Haekchen weg =
// der Punkt wird nicht gedruckt - samt seinem Mangel und seinen Fotos.
//
// Wichtig: In der App bleibt alles stehen. Die Auswahl haengt am Pruefpunkt
// (Spalte nicht_drucken) und laesst sich vor jedem Download wieder aendern.
// Die Maengelliste fuers Angebot druckt weiterhin alles und markiert die
// ausgenommenen Zeilen nur.

import { useState, useEffect, useMemo } from 'react';
import { C } from './formui';
import { supabase, withTimeout } from '../lib/supabase';
import { istBeantwortet } from '../lib/pruefpunktLogik';

// kapitel: [{ id, name, punkte: [Pruefpunkt mit anzeige_nummer] }] - gebaut aus
// ladeProtokollDaten(), damit hier genau die Punkte stehen, die auch im PDF
// landen wuerden (ausgeblendete Folgefragen sind schon heraus).
export default function DruckAuswahl({ kapitel, titel, onAbbrechen, onDrucken }) {
  // Haekchen: true = wird gedruckt.
  const [gedruckt, setGedruckt] = useState({});
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState(null);

  useEffect(() => {
    const start = {};
    (kapitel || []).forEach(k => k.punkte.forEach(pp => { start[pp.id] = !pp.nicht_drucken; }));
    setGedruckt(start);
  }, [kapitel]);

  const alle = useMemo(() => (kapitel || []).flatMap(k => k.punkte), [kapitel]);
  const ausgenommen = alle.filter(pp => gedruckt[pp.id] === false).length;

  const setzeKapitel = (k, wert) => setGedruckt(g => {
    const neu = { ...g };
    k.punkte.forEach(pp => { neu[pp.id] = wert; });
    return neu;
  });

  const drucken = async () => {
    setSpeichert(true); setFehler(null);
    try {
      // Nur die geaenderten Punkte schreiben - in zwei Anfragen (an/aus).
      const einschalten = alle.filter(pp => gedruckt[pp.id] !== false && pp.nicht_drucken).map(pp => pp.id);
      const ausschalten = alle.filter(pp => gedruckt[pp.id] === false && !pp.nicht_drucken).map(pp => pp.id);
      for (const [ids, wert] of [[ausschalten, true], [einschalten, false]]) {
        if (ids.length === 0) continue;
        const { error } = await withTimeout(supabase.from('protokoll_modul_pruefpunkte')
          .update({ nicht_drucken: wert }).in('id', ids), 30000, 'Druckauswahl speichern');
        if (error) throw error;
      }
      // Den lokalen Stand nachziehen, damit die Häkchen beim nächsten Öffnen stimmen.
      alle.forEach(pp => { pp.nicht_drucken = gedruckt[pp.id] === false; });
      await onDrucken();
    } catch (e) {
      setFehler('Die Auswahl konnte nicht gespeichert werden: ' + (e.message || e));
      setSpeichert(false);
      return;
    }
    setSpeichert(false);
  };

  const zeile = (pp) => {
    const an = gedruckt[pp.id] !== false;
    return (
      <label key={pp.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '7px 10px',
                                  borderRadius: 7, cursor: 'pointer', background: an ? 'transparent' : C.orangeBg }}>
        <input type='checkbox' checked={an} onChange={e => setGedruckt(g => ({ ...g, [pp.id]: e.target.checked }))}
          style={{ width: 20, height: 20, marginTop: 2, flexShrink: 0, cursor: 'pointer' }}/>
        <span style={{ fontSize: 14, color: an ? C.navy : C.gray700, lineHeight: 1.35 }}>
          <strong>{pp.anzeige_nummer}</strong> {pp.bezeichnung}
          {pp.wert_auswahl && (
            <span style={{ color: pp.in_ordnung === false ? C.red : C.gray500, fontWeight: 700 }}>
              {' – ' + pp.wert_auswahl}
            </span>
          )}
          {!istBeantwortet(pp) && <span style={{ color: C.gray500 }}> – offen</span>}
        </span>
      </label>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.6)', zIndex: 300,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: C.white, borderRadius: 14, width: '100%', maxWidth: 760, maxHeight: '90vh',
                    display: 'flex', flexDirection: 'column', boxShadow: '0 10px 50px rgba(0,0,0,0.35)' }}>
        <div style={{ padding: '18px 22px 12px' }}>
          <div style={{ fontWeight: 800, fontSize: 18, color: C.navy }}>Was soll gedruckt werden?</div>
          <div style={{ fontSize: 14, color: C.gray700, marginTop: 6, lineHeight: 1.45 }}>
            Für <strong>{titel}</strong>. Häkchen weg = der Punkt erscheint nicht im Dokument, auch sein Mangel
            und seine Fotos nicht. In der App bleibt alles stehen, und vor dem nächsten Download kannst du
            die Auswahl wieder ändern. Die Mängelliste fürs Angebot druckt weiterhin alles und markiert
            die ausgenommenen Punkte.
          </div>
          {ausgenommen > 0 && (
            <div style={{ marginTop: 8, padding: '7px 11px', background: C.orangeBg, borderRadius: 7,
                          fontSize: 14, color: C.navy, fontWeight: 700 }}>
              {ausgenommen === 1 ? '1 Punkt wird nicht gedruckt' : ausgenommen + ' Punkte werden nicht gedruckt'}
            </div>
          )}
          {fehler && (
            <div style={{ marginTop: 8, padding: '9px 12px', background: C.redBg, border: '1.5px solid ' + C.red,
                          borderRadius: 7, fontSize: 14, color: C.red, fontWeight: 700 }}>{fehler}</div>
          )}
        </div>

        <div style={{ overflowY: 'auto', padding: '0 14px', flex: 1 }}>
          {(kapitel || []).map(k => {
            const anzahlAus = k.punkte.filter(pp => gedruckt[pp.id] === false).length;
            return (
              <div key={k.id} style={{ marginBottom: 14, border: '1px solid ' + C.gray200, borderRadius: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                              padding: '9px 12px', background: C.gray100, borderRadius: '9px 9px 0 0' }}>
                  <div style={{ fontWeight: 800, color: C.navy, fontSize: 15 }}>
                    {k.name}
                    {anzahlAus > 0 && <span style={{ color: C.orangeText, fontWeight: 700 }}>{' · ' + anzahlAus + ' nicht im Druck'}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button type='button' onClick={() => setzeKapitel(k, true)}
                      style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid ' + C.gray200,
                               background: C.white, color: C.navy, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>alle</button>
                    <button type='button' onClick={() => setzeKapitel(k, false)}
                      style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid ' + C.gray200,
                               background: C.white, color: C.gray700, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>keinen</button>
                  </div>
                </div>
                <div style={{ padding: '4px 4px 8px' }}>{k.punkte.map(zeile)}</div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 22px',
                      borderTop: '1px solid ' + C.gray200 }}>
          <button type='button' onClick={onAbbrechen} disabled={speichert}
            style={{ minHeight: 44, padding: '10px 18px', borderRadius: 9, border: '1.5px solid ' + C.gray200,
                     background: C.white, color: C.navy, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
            Abbrechen
          </button>
          <button type='button' onClick={drucken} disabled={speichert}
            style={{ minHeight: 44, padding: '10px 20px', borderRadius: 9, border: 'none',
                     background: speichert ? C.gray300 : C.navy, color: C.white, fontWeight: 800, fontSize: 15,
                     cursor: speichert ? 'wait' : 'pointer' }}>
            {speichert ? 'Erstelle PDF …' : 'PDF erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}
